import type { JSX } from 'react';

import { Text } from '@change/ds-core-ui';

import type { QrCodeType } from '@change-corgi/middle-end-gql-schema';

import { PetitionQRCode } from 'src/app/shared/components/qrcode';

import { QRCodeDownloadLink } from './QRCodeDownloadLink';

/**
 * Props:
 * @param {string} slugOrId              The petition slug or ID to generate the QR code for.
 * @param {number} displaySize           Display size in pixels—used to reserve space for the QR image
 *                                       before load and prevent cumulative layout shift.
 * @param {QrCodeType} displayType       Type for the displayed QR code.
 * @param {QrCodeType} downloadType      Type for the downloadable QR code.
 * @param {number} [downloadSize]        Optional size in pixels for the downloadable QR code.
 *                                       If omitted, will use default size configured in GraphQL.
 * @param {string} descriptionText       Text shown beside the QR code explaining its purpose.
 * @param {string} downloadLinkText      Text for the download link.
 * @param {string} [downloadFilename]    Optional filename for the QR code download (defaults to 'petition-qrcode.png').
 * @param {() => Promise<void>} [onDownload] Optional handler called after download is initiated.
 */
export function QRCodeDownload({
	slugOrId,
	displaySize,
	displayType,
	downloadSize,
	downloadType,
	descriptionText,
	downloadLinkText,
	downloadFilename,
	onDownload,
}: {
	slugOrId: string;
	displaySize: number;
	displayType: QrCodeType;
	downloadType: QrCodeType;
	downloadSize?: number;
	descriptionText: string;
	downloadLinkText: string;
	downloadFilename?: string;
	onDownload?: () => Promise<void>;
}): JSX.Element {
	return (
		<div className="flex flex-col gap-4 rounded-lg border border-solid border-neutral-200 p-4">
			<div className="flex justify-center gap-2">
				<div className="h-20 min-h-20 w-20 min-w-20">
					<PetitionQRCode slugOrId={slugOrId} size={displaySize} type={displayType} className="flex-shrink-0" />
				</div>
				<Text variant="body" size="medium">
					{descriptionText}
				</Text>
			</div>

			<div className="flex flex-col gap-2">
				{/* eslint-disable-next-line @change-corgi/require-data-qa-attribute */}
				<QRCodeDownloadLink
					slugOrId={slugOrId}
					size={downloadSize}
					type={downloadType}
					onDownload={onDownload}
					filename={downloadFilename}
				>
					{downloadLinkText}
				</QRCodeDownloadLink>
			</div>
		</div>
	);
}
